z
<template>
  <div class="bg-gray-50">
    <!-- Loading State -->
    <div v-if="saving" class="tw-flex items-center justify-center h-64">
      <div class="text-center">
        <Icon name="spinner" class="w-8 h-8 text-primary animate-spin mx-auto mb-2"/>
        <p class="text-gray-500">{{ $t('knowledge_base_saving_document') }}</p>
      </div>
    </div>

    <!-- Form Content -->
    <div v-else>
      <!-- Hero Section -->
      <div class="relative overflow-hidden bg-gradient-to-br from-blue-50 to-gray-50">
        <div v-if="isReadOnly" class="absolute inset-0 z-10 cursor-not-allowed" style="background: rgba(255,255,255,0.4)"></div>
        <div class="max-w-7xl mx-auto px-4 py-12">
          <form @submit.prevent="submitForm">
            <div class="grid gap-8 lg:grid-cols-2 lg:gap-12">
              <div class="tw-flex flex-col justify-center space-y-6">
                <div class="space-y-4">
                  <div class="tw-flex items-center gap-2">
                    <!-- Category Dropdown -->
                    <div class="relative">
                      <router-link
                        class="!hover:underline"
                        :to="this.spottingAreaId ? `/spotting-areas/${this.spottingAreaId}/knowledge-base` : this.$route.params.communityId ? `/communities/${this.$route.params.communityId}/knowledge-base` : '/dashboards/knowledge-base'"
                        style="text-decoration: none;"
                      >
                        <Icon name="arrow-left" size="14"/>
                        {{ $t('knowledge_base_back_to_knowledge_base') }}
                      </router-link>
                      <FormGroup
                        label="hidden"
                        :error="errors.category_id"
                        is-simplified
                      >
                        <dropdown
                          :placeholder="$t('knowledge_base_select_category_placeholder')"
                          search
                          new-append-icon="chevron-down"
                          is-simplified
                          class="w-48 min-w-48 mt-2"
                          :options="categoryDropdownOptions"
                          v-model="form.category_id"
                        />
                      </FormGroup>

                      <div
                        v-if="showCategoryDropdown"
                        class="absolute z-10 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200"
                      >
                        <div class="py-1">
                          <!-- Create New Category Option (for team members and owners only) -->
                          <div v-if="canCreateCategory" class="border-t border-gray-200">
                            <DsButton
                              @click="openCreateCategoryModal"
                              type="button"
                              :label="$t('knowledge_base_create_new_category')"
                              class="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors font-medium"
                            >
                            </DsButton>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- File Type Display -->
                    <span
                      v-if="uploadedFiles.length > 0"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border"
                    >
                      {{ getFileTypeLabel(uploadedFiles[0].type) }}
                    </span>
                  </div>

                  <!-- Title Input -->
                  <FormGroup
                    label="hidden"
                    :error="errors.title"
                    required
                  >
                    <input
                      v-model="form.title"
                      type="text"
                      required
                      class="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl !bg-transparent border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 w-full px-0 py-2"
                      :class="{ 'border-red-500 focus:border-red-500': errors.title }"
                      :placeholder="$t('knowledge_base_document_title_placeholder')"
                    />
                  </FormGroup>

                  <!-- Excerpt Input -->
                  <FormGroup
                    label="hidden"
                    :error="errors.excerpt"
                  >
                    <DsTextarea
                      is-simplified
                      v-model="form.excerpt"
                      rows="3"
                      class="text-base text-gray-600 !bg-transparent border-0 border-b border-gray-300 focus:border-blue-500 focus:ring-0 w-full px-0 py-2 resize-none min-h-24 max-h-24"
                      :class="{ 'border-red-500 focus:border-red-500': errors.excerpt }"
                      :defaultText="$t('knowledge_base_brief_summary_placeholder')"
                      :max-length="300"
                      autoresize
                    />
                  </FormGroup>
                </div>

                <div class="tw-flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <div class="tw-flex items-center gap-1">
                    <Icon name="user" class="h-4 w-4"/>
                    <span>{{ currentUserName || $t('knowledge_base_you') }}</span>
                  </div>
                  <div class="tw-flex items-center gap-1">
                    <Icon name="clock" class="h-4 w-4"/>
                    <span>{{ $t('knowledge_base_creating_now') }}</span>
                  </div>
                  <div v-if="uploadedFiles.length > 0" class="tw-flex items-center gap-1">
                    <Icon name="paperclip" class="h-4 w-4"/>
                    <span>{{ uploadedFiles.length }} {{
                        uploadedFiles.length === 1 ? $t('knowledge_base_files_attached_single') : $t('knowledge_base_files_attached_multiple')
                      }}</span>
                  </div>
                </div>
              </div>

              <!-- Featured Image Upload Area -->
              <FormGroup
                label="hidden"
                :error="errors.thumbnail"
              >
                <div class="relative">
                  <div
                    v-if="!featuredImagePreview"
                    @click="triggerFeaturedImageInput"
                    @dragover.prevent
                    @drop.prevent="handleFeaturedImageDrop"
                    class="rounded-lg border-2 border-dashed w-full h-64 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    :class="{
                      'border-red-300 bg-red-50': errors.thumbnail,
                      'border-gray-300': !errors.thumbnail
                    }"
                  >
                    <Icon name="photo" class="w-12 h-12 text-gray-400 mb-2"/>
                    <p class="text-sm text-gray-600 font-medium">{{ $t('knowledge_base_add_thumbnail_image') }}</p>
                    <p class="text-xs text-gray-500 mt-1">{{ $t('knowledge_base_click_or_drag_upload') }}</p>
                    <p class="text-xs text-gray-500 mt-2 font-medium text-center w-3/4">
                      {{ $t('knowledge_base_thumbnail_auto_generate_note') }}
                    </p>
                  </div>
                  <div v-else class="relative">
                    <img
                      :src="featuredImagePreview"
                      :alt="$t('knowledge_base_featured_image_preview_alt')"
                      class="rounded-lg shadow-lg w-full h-[300px] object-cover"
                    />
                    <button
                      @click="removeFeaturedImage"
                      type="button"
                      class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                    >
                      <Icon name="x" class="w-4 h-4"/>
                    </button>
                  </div>
                  <input
                    ref="featuredImageInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFeaturedImageSelect"
                  />
                </div>
              </FormGroup>
            </div>
          </form>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 py-8">
        <form @submit.prevent="submitForm">
          <!-- Read-only overlay when another user holds the lock -->
          <div v-if="lockHolder && isReadOnly" class="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div class="tw-flex items-start gap-2 items-center">
              <Icon name="lock" class="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"/>
              <p class="text-xs text-red-800 self-center">
                <strong>{{ lockHolder.name }}</strong> {{ $t('knowledge_base_lock_held_by_other') }}
              </p>
            </div>
          </div>
          <div class="relative">
            <!-- Interaction blocker when locked -->
            <div v-if="isReadOnly" class="absolute inset-0 z-10 cursor-not-allowed" style="background: rgba(255,255,255,0.4)"></div>
            <div class="grid gap-8 lg:grid-cols-3">
              <!-- Main Content -->
              <div class="lg:col-span-2 space-y-8">
                <!-- Description -->
                <div class="bg-white rounded shadow-sm border">
                  <div class="p-4 border-b">
                    <h2 class="text-base font-semibold text-gray-900 uppercase">{{ $t('knowledge_base_content') }}</h2>
                    <p class="text-sm text-gray-600 mt-1">
                      {{ $t('knowledge_base_detailed_information_about_this_post') }}
                    </p>
                  </div>
                  <div class="p-4">
                    <FormGroup
                      label="hidden"
                      :error="errors.body"
                    >
                      <div class="space-y-3">
                        <!-- AI Generation Section -->
                        <div v-if="canAutoGenerateContent">
                          <DsInput
                            icon="robot"
                            icon-color="#843DFF"
                            v-model="generationInstructions"
                            ai-variant
                            is-simplified
                            :placeholder="hasRequiredContentForGeneration ? $t('knowledge_base_ai_placeholder_ready') : $t('knowledge_base_ai_placeholder_not_ready')"
                            :disabled="isGeneratingContent || !hasRequiredContentForGeneration"
                          />
                          <span v-if="hasContent" class="text-xs text-red-600 mt-1 block">
                          {{ $t('knowledge_base_ai_overwrite_warning') }}
                        </span>
                          <div class="flex mt-2 gap-2">
                            <ds-button
                              variant="accent-rounded"
                              size="small"
                              :disabled="isGeneratingContent || !hasRequiredContentForGeneration"
                              :label="generateButtonLabel"
                              :loading="isGeneratingContent"
                              @click="handleGenerate"
                            />
                          </div>
                        </div>

                        <RichTextArea
                          v-model="form.body"
                          rows="12"
                          :no-height-limit="true"
                          class="w-full rounded shadow-sm focus:outline-none resize-none"
                          :class="{
                          'focus:ring-red-500 focus:border-red-500 border-red-300': errors.body,
                          'focus:ring-blue-500 focus:border-blue-500': !errors.body
                        }"
                        ></RichTextArea>
                      </div>
                    </FormGroup>
                  </div>
                </div>

                <!-- Landscape Configuration -->
                <div class="bg-white rounded shadow-sm border" v-if="isMember && isUserOfDataScoutsTeam">
                  <div class="p-4 border-b">
                    <h2 class="text-base font-semibold text-gray-900 uppercase">{{ $t('knowledge_base_landscape') }}</h2>
                    <p class="text-sm text-gray-600 mt-1">
                      {{ $t('knowledge_base_mark_post_as_landscape_description') }}
                    </p>
                  </div>
                  <div class="p-4 space-y-4">
                    <!-- Mark as Landscape Checkbox -->
                    <div class="tw-flex items-center gap-3">
                      <input
                        id="markAsLandscape"
                        type="checkbox"
                        v-model="form.is_landscape"
                        class="h-4 w-4 mb-2 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <!--                      :disabled="!spottingAreaId || !hasSpottingAreaMaps"-->
                      <label for="markAsLandscape" class="text-sm font-medium text-gray-700">
                        {{ $t('knowledge_base_mark_post_as_landscape') }}
                      </label>
                    </div>

                    <!-- Location & Range Configuration (shown when checked) -->
                    <div v-if="form.is_landscape" class="space-y-4 mt-4 pt-4 border-t">
                      <!-- Location & Range Summary -->
                      <div class="space-y-2">
                        <label class="block text-sm font-medium text-gray-700">
                          {{ $t('knowledge_base_location_and_range') }}
                        </label>
                        <div class="grid grid-cols-3 gap-2 text-sm bg-gray-50 p-3 rounded">
                          <div>
                            <label class="text-xs text-gray-500">{{ $t('knowledge_base_latitude') }}</label>
                            <p class="font-medium">{{ form.landscape_lat ? parseFloat(form.landscape_lat).toFixed(6) : $t('knowledge_base_not_set') }}</p>
                          </div>
                          <div>
                            <label class="text-xs text-gray-500">{{ $t('knowledge_base_longitude') }}</label>
                            <p class="font-medium">{{ form.landscape_lng ? parseFloat(form.landscape_lng).toFixed(6) : $t('knowledge_base_not_set') }}</p>
                          </div>
                          <div>
                            <label class="text-xs text-gray-500">{{ $t('knowledge_base_range') }}</label>
                            <p class="font-medium">{{ form.landscape_range ? `${form.landscape_range} km` : $t('knowledge_base_not_set') }}</p>
                          </div>
                        </div>
                        <ds-button
                          variant="rounded-secondary"
                          :label="$t('knowledge_base_set_on_map')"
                          icon="map"
                          size="small"
                          @click="openLandscapeMapModal"
                          class="w-full"
                        />
                        <p class="text-xs text-gray-500">
                          {{ $t('knowledge_base_click_to_set_location_and_range') }}
                        </p>
                      </div>

                      <!-- Portfolio Selection -->
                      <FormGroup
                        :label="$t('knowledge_base_linked_portfolio')"
                        :error="errors.landscape_portfolio_id"
                        label-class="text-sm font-medium text-gray-700"
                      >
                        <dropdown
                          :placeholder="$t('knowledge_base_select_portfolio')"
                          search
                          new-append-icon="chevron-down"
                          is-simplified
                          :options="availablePortfoliosDropdownOptions"
                          v-model="form.landscape_portfolio_id"
                        />
                        <p class="text-xs text-gray-500 mt-1">
                          {{ $t('knowledge_base_portfolio_description') }}
                        </p>
                      </FormGroup>

                      <!-- Hide circle toggle -->
                      <div class="tw-flex items-center gap-3 pt-2">
                        <input
                          id="landscapeCircleHidden"
                          type="checkbox"
                          v-model="form.landscape_circle_hidden"
                          class="h-4 w-4 mb-2 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label for="landscapeCircleHidden" class="text-sm font-medium text-gray-700">
                          {{ $t('knowledge_base_landscape_circle_hidden') }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sidebar -->
              <div class="space-y-4">
                <!-- Files Section -->
                <div class="bg-white rounded shadow-sm border">
                  <div class="p-4 border-b">
                    <h3 class="text-base font-semibold text-gray-900 uppercase">{{ $t('knowledge_base_attach_files') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">
                      {{ $t('knowledge_base_documents_media_and_resources_related_to_this_post') }}
                    </p>
                  </div>
                  <div class="p-4 space-y-4">
                    <!-- File Upload Area -->
                    <div
                      @click="triggerFileInput"
                      @dragover.prevent
                      @drop.prevent="handleFileDrop"
                      class="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <Icon name="cloud-upload" class="w-8 h-8 mx-auto mb-2 text-gray-400"/>
                      <p class="text-sm text-gray-600 font-medium">{{ $t('knowledge_base_click_to_upload') }}</p>
                      <p class="text-xs text-gray-500 mt-1">{{ $t('knowledge_base_or_drag_drop') }}</p>
                      <p class="text-xs text-gray-500">Max 40 MB.</p>
                    </div>

                    <!-- URL Input -->
                    <div class="border-t pt-4">
                      <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('knowledge_base_add_url') }}</label>
                      <div class="tw-flex gap-2">
                        <input
                          v-model="urlInput"
                          type="url"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                          :placeholder="$t('knowledge_base_url_placeholder')"
                        />
                        <ds-button
                          @click="addUrl"
                          type="button"
                          variant="rounded"
                          size="small"
                          :disabled="!urlInput.trim()"
                          :label="$t('add')"
                        >
                        </ds-button>
                      </div>
                    </div>

                    <!-- File Previews -->
                    <div v-if="uploadedFiles.length > 0" class="space-y-2">
                      <div
                        v-for="(file, index) in uploadedFiles"
                        :key="index"
                        :class="[
                        'p-3 rounded-md border transition-all duration-200 hover:shadow-sm',
                        file.isFeatured
                          ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      ]"
                      >
                        <div class="tw-flex items-center justify-between">
                          <!-- File info -->
                          <div class="tw-flex items-center space-x-3 flex-1 min-w-0">
                            <Icon
                              :name="getFileIcon(file.type || file.name)"
                              class="w-5 h-5 text-blue-600 flex-shrink-0"
                            />
                            <div class="flex-1 min-w-0">
                              <div class="tw-flex items-center gap-2 mb-1">
                                <!-- Editable filename -->
                                <div class="flex-1 min-w-0">
                                  <input
                                    v-if="file.isEditingName"
                                    v-model="file.editedName"
                                    @blur="saveFileName(index, file)"
                                    @keydown.enter="saveFileName(index, file)"
                                    @keydown.esc="cancelEditFileName(index, file)"
                                    type="text"
                                    class="text-sm font-medium text-gray-900 bg-white border border-blue-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    ref="fileNameInput"
                                  />
                                  <div
                                    v-else
                                    @click.stop="startEditFileName(index, file)"
                                    class="items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors group tw-flex flex-row"
                                    :title="$t('knowledge_base_click_to_edit_filename') + ': ' + (file.displayName || file.name || file.url)"
                                  >
                                    <h4 class="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                                      {{ file.displayName || file.name || file.url }}
                                    </h4>
                                    <Icon
                                      name="pencil" size="12"
                                      class="w-3 h-3 text-gray-400 group-hover:text-blue-600 flex-shrink-0"
                                    />
                                  </div>
                                </div>
                                <button
                                  @click="toggleFeatureFile(index, file)"
                                  type="button"
                                  :class="[
                                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium transition-colors flex-shrink-0',
                                    file.isFeatured
                                      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                      : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-800'
                                  ]"
                                  :title="file.isFeatured ? $t('knowledge_base_remove_as_featured') : $t('knowledge_base_set_as_featured')"
                                >
                                  {{ file.isFeatured ? $t('knowledge_base_featured') : $t('knowledge_base_feature') }}
                                </button>
                              </div>
                              <div class="tw-flex items-center justify-between">
                                <p v-if="file.size" class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
                              </div>
                            </div>
                          </div>

                          <!-- Remove File Button -->
                          <button
                            @click="removeFile(index)"
                            type="button"
                            class="text-gray-400 hover:text-red-500 p-1 ml-2 transition-colors"
                            :title="$t('knowledge_base_remove_file')"
                          >
                            <Icon name="x" class="w-4 h-4"/>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div v-if="uploadedFiles.length === 0" class="text-center py-4 text-gray-500">
                      <Icon name="document" class="w-8 h-8 mx-auto mb-2 text-gray-300"/>
                      <p class="text-sm">{{ $t('knowledge_base_no_files_attached_yet') }}</p>
                    </div>

                    <!-- File and URL Error Messages -->
                    <FormGroup
                      v-if="errors.files || errors.urls"
                      label="hidden"
                      :error="errors.files || errors.urls"
                    >
                    </FormGroup>

                    <input
                      ref="fileInput"
                      type="file"
                      multiple
                      accept="*/*"
                      class="hidden"
                      @change="handleFileSelect"
                    />
                  </div>
                </div>

                <!-- Publishing Actions -->
                <div class="bg-white rounded shadow-sm border">
                  <div class="p-4 border-b">
                    <h3 class="text-base font-semibold text-gray-900 uppercase">{{ $t('knowledge_base_publishing') }}</h3>
                  </div>
                  <div class="p-4 space-y-3">
                    <!-- Community Selection -->
                    <FormGroup
                      v-if="availableCommunitiesDropdownOptions.length > 0 && !isContributorOnly"
                      label="hidden"
                      :error="errors.community_ids"
                      instructions=""
                    >
                      <div class="relative">
                        <label class="block text-xs font-medium text-gray-500 mb-2">{{ $t('knowledge_base_community_selection_info') }}</label>
                        <dropdown
                          :placeholder="$t('knowledge_base_select_community')"
                          search
                          multiple
                          new-append-icon="chevron-down"
                          is-simplified
                          class="w-48 min-w-48"
                          :options="availableCommunitiesDropdownOptions"
                          :modelValue="form.community_ids"
                          @update:modelValue="handleCommunityIdsChange"
                        />
                      </div>
                    </FormGroup>

                    <FormGroup
                      v-if="canAssignSpottingAreasToPost && !isContributorOnly"
                      label="hidden"
                      :error="errors.spotting_area_ids"
                      instructions=""
                    >
                      <div class="relative">
                        <label class="block text-xs font-medium text-gray-500 mb-2">{{
                            $t('knowledge_base_spotting_area_selection_info', { spottingarea: spottingAreaLabel })
                          }}</label>
                        <dropdown
                          class="w-48 min-w-48"
                          search
                          multiple
                          new-append-icon="chevron-down"
                          :modelValue="form.spotting_area_ids"
                          @update:modelValue="handleSpottingAreaIdsChange"
                          is-simplified
                          :options="availableSpottingAreasDropdownOptions"
                          :placeholder="$t('knowledge_base_select_spotting_area', { spottingarea: spottingAreaLabel })"
                        />
                      </div>
                    </FormGroup>

                    <!-- Show in public knowledge base (only relevant when community/spotting area is selected) -->
                    <div v-if="(form.community_ids.length > 0 || form.spotting_area_ids.length > 0) && !isContributorOnly">
                      <label class="tw-flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          class="mt-0.5 shrink-0"
                          v-model="form.show_in_public_knowledge_base"
                        />
                        <span class="text-xs text-gray-600 mt-1">{{ $t('knowledge_base_show_in_public_knowledge_base') }}</span>
                      </label>
                    </div>

                    <hr class="border-gray-200">

                    <!-- Contributors (only visible when editing an existing post) -->
                    <div v-if="isEditing">
                      <div class="tw-flex items-center justify-between mb-2">
                        <label class="block text-xs font-medium text-gray-500">{{ $t('knowledge_base_contributors') }}</label>
                        <button
                          v-if="canManageContributors"
                          type="button"
                          class="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          @click="showContributorsModal = true"
                        >
                          {{ $t('knowledge_base_manage_contributors') }}
                        </button>
                      </div>
                      <!-- Editor presence -->
                      <KnowledgeBaseEditorPresence
                        :editors="activeEditors"
                        :current-user-id="currentUserId"
                      />
                    </div>

                    <hr v-if="isEditing" class="border-gray-200">

                    <!-- Locked by another user banner -->
                    <div v-if="lockHolder" class="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div class="tw-flex items-start gap-2">
                        <Icon name="lock" class="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"/>
                        <p class="text-xs text-red-800">
                          <strong>{{ lockHolder.name }}</strong> {{ $t('knowledge_base_lock_held_by_other') }}
                        </p>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="space-y-2">
                      <ds-button
                        type="submit"
                        :loading="saving"
                        :disabled="!isFormValid || (isEditing && !lockToken)"
                        :label="submitButtonText"
                        icon="check"
                        variant="primary"
                        class="w-full"
                      />

                      <ds-button
                        @click="goBack"
                        type="button"
                        :label="$t('cancel')"
                        variant="ghost"
                        class="w-full"
                      />
                    </div>
                  </div>
                </div>

                <!-- Preview -->
                <div class="bg-white rounded shadow-sm border mb-20">
                  <div class="p-4 border-b">
                    <h3 class="text-base font-semibold text-gray-900 uppercase">{{ $t('knowledge_base_preview') }}</h3>
                  </div>
                  <div class="p-4 space-y-3">
                    <div class="text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_title') }}:</span>
                      <span class="text-gray-900 ml-1 font-medium">{{ form.title || $t('knowledge_base_untitled') }}</span>
                    </div>
                    <div class="text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_category_label') }} :</span>
                      <span class="text-gray-900 ml-1">{{ getCategoryDisplayName() }}</span>
                    </div>
                    <div class="text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_community') }}:</span>
                      <span class="text-gray-900 ml-1">{{ getCommunityDisplayName() }}</span>
                    </div>
                    <div class="text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_files') }}:</span>
                      <span class="text-gray-900 ml-1">{{ uploadedFiles.length }} {{ $t('knowledge_base_attached') }}</span>
                    </div>
                    <div v-if="featuredImage" class="text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_featured_image') }}:</span>
                      <span class="text-green-600 ml-1">✓ {{ $t('knowledge_base_added') }}</span>
                    </div>
                    <div v-if="uploadedFiles.some(f => f.isFeatured)" class="text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_featured_file') }}:</span>
                      <span class="text-blue-600 ml-1">{{ getFeaturedFileName() }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div><!-- end relative wrapper -->
        </form>
      </div>
    </div>

    <!-- Contributors Modal -->
    <KnowledgeBaseContributorsModal
      v-if="showContributorsModal && isEditing"
      :post-id="Number(documentId)"
      :contributors="contributors"
      :community-managers="communityManagers"
      :can-manage="canManageContributors"
      :community-id="communityId ? Number(communityId) : null"
      :creator="postCreator"
      :current-user-id="currentUserId"
      @close="showContributorsModal = false"
      @contributors-updated="loadContributors"
    />

    <!-- Landscape Map Selection Modal -->
    <div v-if="showLandscapeMapModal" class="fixed inset-0 bg-black/70 tw-flex items-center justify-center backdrop-blur-md" style="z-index: 9999;">
      <div class="bg-white rounded-3xl shadow-2xl w-[95vw] h-[90vh] max-w-7xl tw-flex flex-col overflow-hidden border border-gray-200/50"
           style="position: relative; z-index: 10000;">
        <!-- Modal Header -->
        <div class="px-8 py-6 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 tw-flex items-center justify-between relative overflow-hidden"
             style="position: relative; z-index: 10002;">
          <!-- Decorative background pattern -->
          <div class="absolute inset-0 opacity-10">
            <div class="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div class="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div class="relative z-10 flex-1">
            <div class="tw-flex items-center gap-3 mb-2">
              <h3 class="text-2xl font-bold text-black">
                {{ $t('knowledge_base_set_landscape_location') }}
              </h3>
            </div>
            <p class="text-sm text-primary-50">{{ $t('knowledge_base_click_map_to_set_center') }}</p>
          </div>
          <button
            @click="closeLandscapeMapModal"
            class="relative z-10 text-white/80 hover:text-white hover:bg-white/20 p-3 rounded-xl transition-all hover:rotate-90 duration-300"
          >
            <Icon name="x" class="w-6 h-6"/>
          </button>
        </div>

        <!-- Main Content: Map and Settings Side by Side -->
        <div class="tw-flex flex-row flex-1 overflow-hidden">
          <!-- Map Container (Left Side) -->
          <div class="flex-1 p-8 bg-gray-50" style="position: relative; z-index: 1; overflow: hidden;">
            <div id="landscape-map-selector" class="w-full h-full rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden" style="position: relative; z-index: 1;"></div>
          </div>

          <!-- Range Control Panel (Right Side) -->
          <div class="w-1/3 px-8 py-6 bg-gradient-to-br from-gray-50 to-white border-l border-gray-200/80 overflow-y-auto" style="position: relative; z-index: 10002;">
            <div class="mx-auto tw-flex flex-col w-full gap-6">
              <!-- City Search + Color Picker -->
              <div class="tw-flex flex-col gap-6 w-full">
                <!-- City Search -->
                <div class="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div class="tw-flex gap-2">
                    <input
                      v-model="citySearchQuery"
                      type="text"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      :placeholder="$t('knowledge_base_city_search_placeholder')"
                      @keydown.enter.prevent="searchCityInModal"
                      @input="citySearchResults = []"
                    />
                    <ds-button
                      @click="searchCityInModal"
                      type="button"
                      variant="rounded"
                      size="small"
                      icon="search"
                      :disabled="!citySearchQuery.trim() || searchingCity"
                      :loading="searchingCity"
                      :label="$t('search')"
                    />
                  </div>

                  <!-- City Results Dropdown -->
                  <div v-if="citySearchResults.length > 0" class="mt-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-lg">
                    <button
                      v-for="city in citySearchResults"
                      :key="city.geoid"
                      @click="selectCity(city)"
                      class="w-full text-left px-3 py-2 hover:bg-primary-50 transition-colors text-sm border-b border-gray-100 last:border-b-0"
                    >
                      {{ city.display_name }}
                    </button>
                  </div>
                </div>

                <!-- Circle Color Selection -->
                <div class="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div class="tw-flex items-center gap-3 mb-4">
                    <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 tw-flex items-center justify-center shadow-md">
                      <Icon name="paint-brush" class="w-5 h-5 text-black" fill="black"/>
                    </div>
                    <label class="text-base font-bold text-gray-800">
                      {{ $t('knowledge_base_circle_color') }}
                    </label>
                  </div>

                  <!-- Stylized Color Picker -->
                  <div class="relative group cursor-pointer">
                    <!-- Visual Wrapper -->
                    <div
                      class="relative w-full rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 border-2 border-gray-200 group-hover:border-primary-400 group-hover:scale-[1.02]">
                      <!-- Color Display -->
                      <div
                        class="w-full h-12 relative transition-all duration-300"
                        :style="{ backgroundColor: tempLandscapeConfig.color }"
                      >
                        <!-- Gradient Overlay for depth -->
                        <div class="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/15"></div>

                        <!-- Animated shine effect on hover -->
                        <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <div
                            class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        </div>

                        <!-- Color Code Badge -->
                        <div class="absolute top-2 left-2 transform transition-transform duration-300 group-hover:scale-105">
                          <div class="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
                            <div class="text-xs font-bold font-mono text-gray-900 tracking-wide">
                              {{ tempLandscapeConfig.color.toUpperCase() }}
                            </div>
                          </div>
                        </div>

                        <!-- Click to Change Indicator -->
                        <div class="absolute bottom-2 right-2 transform transition-all duration-300 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0">
                          <div class="bg-gradient-to-r from-primary-600 to-primary-700 px-3 py-1.5 rounded-lg shadow-lg tw-flex items-center gap-1.5">
                            <Icon name="paint-brush" class="w-3.5 h-3.5 text-black" fill="black"/>
                            <span class="text-xs font-semibold">Change</span>
                          </div>
                        </div>
                      </div>

                      <!-- Hidden Native Color Input (positioned absolutely to cover the entire area) -->
                      <input
                        type="color"
                        v-model="tempLandscapeConfig.color"
                        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>

                  <p class="text-xs text-gray-500 mt-3 leading-relaxed">
                    {{ $t('knowledge_base_circle_color_description') }}
                  </p>
                </div>
              </div>

              <!-- Range Slider -->
              <div class="w-full">
                <!-- Range Display Card -->
                <div class="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div class="tw-flex items-center justify-between mb-4">
                    <div class="tw-flex items-center gap-3">
                      <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 tw-flex items-center justify-center shadow-md">
                        <Icon name="resize" class="w-5 h-5 text-black" fill="black"/>
                      </div>
                      <label class="text-base font-bold text-gray-800">
                        {{ $t('knowledge_base_range_radius') }}
                      </label>
                    </div>
                    <div class="tw-flex items-baseline gap-2 bg-gradient-to-br from-primary-50 to-blue-50 px-5 py-3 rounded-xl shadow-sm border border-primary-200">
                      <span class="text-3xl font-bold text-primary-700">{{ tempLandscapeConfig.range }}</span>
                      <span class="text-sm font-semibold text-gray-600">{{ $t('knowledge_base_kilometers') }}</span>
                    </div>
                  </div>

                  <!-- Slider Control -->
                  <div class="tw-flex items-center gap-4 mb-5">
                    <button
                      @click="decrementRange"
                      class="w-11 h-11 rounded-xl bg-white border-2 border-gray-300 hover:border-primary-500 hover:bg-gradient-to-br hover:from-primary-50 hover:to-blue-50 transition-all tw-flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105"
                      title="Decrease by 10 km"
                    >
                      <Icon name="minus" class="w-5 h-5 text-gray-700"/>
                    </button>
                    <div class="flex-1 relative">
                      <input
                        v-model.number="tempLandscapeConfig.range"
                        type="range"
                        :min="0.5"
                        :max="500"
                        step="0.5"
                        class="w-full h-3 bg-gradient-to-r from-primary-200 to-primary-300 rounded-full appearance-none cursor-pointer slider shadow-inner"
                        @input="updateCircleRadius"
                      />
                      <!-- Range scale markers -->
                      <div class="tw-flex justify-between text-xs font-medium text-gray-500 mt-2 px-1">
                        <span>0.5</span>
                        <span>250</span>
                        <span>500</span>
                      </div>
                    </div>
                    <button
                      @click="incrementRange"
                      class="w-11 h-11 rounded-xl bg-white border-2 border-gray-300 hover:border-primary-500 hover:bg-gradient-to-br hover:from-primary-50 hover:to-blue-50 transition-all tw-flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105"
                      title="Increase by 10 km"
                    >
                      <Icon name="plus" class="w-5 h-5 text-gray-700"/>
                    </button>
                  </div>

                  <!-- Quick Preset Buttons -->
                  <div class="tw-flex items-center gap-3 flex-wrap">
                    <span class="text-sm font-semibold text-gray-700">{{ $t('knowledge_base_quick_select') }}:</span>
                    <button
                      v-for="preset in [5, 10, 25, 50, 100]"
                      :key="preset"
                      @click="setRangePreset(preset)"
                      class="px-4 py-2 text-sm font-semibold rounded-xl border-2 transition-all shadow-sm hover:shadow-md transform hover:scale-105"
                      :class="tempLandscapeConfig.range === preset
                    ? 'bg-gradient-to-br from-primary-600 to-primary-700 border-primary-600 text-primary shadow-lg scale-105'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-gradient-to-br hover:from-primary-50 hover:to-blue-50'"
                    >
                      {{ preset }} km
                    </button>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="tw-flex flex-col gap-4">
                <!--                <div class="tw-flex items-center gap-2 text-sm text-gray-600 bg-blue-50 px-4 py-2.5 rounded-xl border border-blue-100">
                                  <Icon name="info" class="w-4 h-4 text-blue-600"/>
                                  <span class="font-medium">Click anywhere on the map to reposition the center</span>
                                </div>-->
                <div class="tw-flex gap-3 justify-end">
                  <ds-button
                    variant="rounded-secondary"
                    :label="$t('cancel')"
                    @click="closeLandscapeMapModal"
                    size="lg"
                  />
                  <ds-button
                    variant="rounded"
                    :label="$t('knowledge_base_apply_settings')"
                    @click="applyLandscapeMapSettings"
                    icon="check"
                    size="lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import {
    acquireLock,
    createPost,
    fetchCategories,
    fetchContributors,
    fetchPost,
    getLockStatus,
    refreshLock,
    releaseLock,
    startEditing,
    stopEditing,
    updatePost,
  } from '@/api/knowledge-base'
  import { generateContent } from '@/api/generative'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES } from '@/store/modules/communities'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import DsTextarea from '@/components/Form/DsTextarea.vue'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import DropdownButton from '@/components/DropdownButton/DropdownButton.vue'
  import UserMixin from '@/util/UserMixin'
  import CommunityMixin from '@/util/CommunityMixin'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import { ACTION_TYPES } from '@/store/modules/spottingareas'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import { Portfolios } from '@/api/portfolios'
  import ColorPicker from '@/components/Form/ColorPicker.vue'
  import KnowledgeBaseContributorsModal from './KnowledgeBaseContributorsModal.vue'
  import KnowledgeBaseEditorPresence from './KnowledgeBaseEditorPresence.vue'

  export default defineComponent({
    name: 'KnowledgeBaseCreate',
    mixins: [TranslationsMixin, UserMixin, CommunityMixin, SpottingAreaMixin],
    components: {
      ColorPicker,
      DropdownButton,
      Dropdown,
      DsInput,
      RichTextArea,
      DsTextarea,
      FormGroup,
      Icon,
      KnowledgeBaseContributorsModal,
      KnowledgeBaseEditorPresence,
    },
    data() {
      return {
        form: {
          title: '',
          category_id: null,
          excerpt: '',
          body: '',
          visibility: 'public',
          community_ids: [],
          spotting_area_ids: [],
          show_in_public_knowledge_base: false,
          is_landscape: false,
          landscape_lat: null,
          landscape_lng: null,
          landscape_range: null,
          landscape_portfolio_id: null,
          landscape_color: null,
          landscape_circle_hidden: false,
        },
        categories: [],
        availableCommunities: [],
        availableSpottingAreas: [],
        availablePortfolios: [],
        mapsUsingCategory: [],
        loadingMapsUsage: false,
        showCommunityDropdown: false,
        featuredImage: null,
        featuredImagePreview: null,
        uploadedFiles: [],
        removedFileIds: [], // Track IDs of existing files that should be deleted
        shouldRemoveThumbnail: false, // Track if thumbnail should be removed during editing
        originallyHadThumbnail: false, // Track if post originally had a thumbnail
        urlInput: '',
        errors: {},
        saving: false,
        savingDraft: false,
        showCategoryDropdown: false,
        isGeneratingContent: false,
        showGenerationInput: false,
        generationInstructions: '',
        showLandscapeMapModal: false,
        tempLandscapeConfig: {
          lat: 50.0,
          lng: 15.0,
          range: 5,
          color: '#3b82f6',
        },
        leafletMap: null,
        landscapeCircle: null,
        citySearchQuery: '',
        searchingCity: false,
        citySearchResults: [],
        // Contributors & editing presence
        showContributorsModal: false,
        contributors: [] as any[],
        communityManagers: [] as any[],
        activeEditors: [] as any[],

        editingHeartbeatInterval: null as ReturnType<typeof setInterval> | null,
        autosaveInterval: null as ReturnType<typeof setInterval> | null,
        lastKnownUpdatedAt: null as string | null,
        showDraftRestoredBanner: false,
        // Exclusive edit lock
        lockToken: null as string | null,
        lockHolder: null as { id: number; name: string; photo_url: string | null } | null,
        lockRefreshInterval: null as ReturnType<typeof setInterval> | null,
        lockPollInterval: null as ReturnType<typeof setInterval> | null,
        postCreatorId: null as number | null,
        postCreator: null as any,
      }
    },
    computed: {
      canAssignSpottingAreasToPost() {
        if (this.$store.getters.isMember) {
          return true
        }

        // If we are in a spotting area context and the user is a portfolio member, check if they contribute to this spotting area
        if (this.$store.getters.isPortfolioMember) {
          return this.$store.getters.contributeSpottingAreaIds.length > 0
        }

        // Ecosystem members cannot assign spotting areas to KB posts
        return false
      },
      postId() {
        return this.$route.params.documentId
      },
      availableCommunitiesDropdownOptions() {
        const options = []

        const communityOptions = this.availableCommunities.map((community) => {
          return {
            value: community.id,
            label: community.name
          }
        })

        return options.concat(communityOptions)
      },
      availableSpottingAreasDropdownOptions() {
        const options = this
          .availableSpottingAreas
          .map((spottingArea) => {
            return {
              value: spottingArea.id,
              label: spottingArea.name
            }
          })

        if (this.$store.getters.isMember) {
          return options
        }

        if (this.$store.getters.isPortfolioMember) {
          // Filter to only spotting areas the user contributes to
          const contributeSpottingAreaIds = this.$store.getters.contributeSpottingAreaIds
          return options.filter(option => contributeSpottingAreaIds.includes(option.value))
        }

        // Ecosystem members cannot assign spotting areas to KB posts
        return false
      },
      availablePortfoliosDropdownOptions() {
        return this.availablePortfolios.map((portfolio) => {
          return {
            value: portfolio.id,
            label: portfolio.name
          }
        })
      },
      categoryDropdownOptions() {
        const options = [
          {
            value: null,
            label: this.$t('knowledge_base_no_category')
          }
        ]

        const categoryOptions = this.categories.map((category) => {
          return {
            value: category.id,
            label: category.name
          }
        })

        return options.concat(categoryOptions)
      },
      spottingAreaId(): Number | null {
        if (this.$route.params.spottingAreaId) {
          return Number(this.$route.params.spottingAreaId)
        }

        return null
      },
      communityId(): Number | null {
        if (this.$route.params.communityId) {
          return Number(this.$route.params.communityId)
        }

        return null
      },
      documentId() {
        return this.$route.params.documentId
      },
      isEditing() {
        return !!this.documentId
      },
      isReadOnly() {
        return this.isEditing && !this.lockToken
      },
      pageTitle() {
        return this.isEditing ? this.$t('knowledge_base_edit_post') : this.$t('knowledge_base_create_post')
      },
      submitButtonText() {
        return this.isEditing ? this.$t('knowledge_base_update_post') : this.$t('knowledge_base_create_post')
      },
      isFormValid() {
        return true
      },
      currentUserName() {
        return this.$store.getters.userName || this.$t('knowledge_base_you')
      },
      canCreateCategory() {
        return this.$store.getters.isOwner || this.$store.getters.isMember
      },
      currentUserId(): number {
        return this.$store.getters.userId || this.$store.state.user.profile?.id
      },
      canManageContributors(): boolean {
        if (this.isPostCreator) return true
        if (this.communityId) {
          return this.communityManagers.some((m: any) => Number(m.id) === this.currentUserId)
        }
        return false
      },
      isPostCreator(): boolean {
        return this.postCreatorId !== null && this.postCreatorId === this.currentUserId
      },
      isContributorOnly(): boolean {
        if (!this.isEditing) return false
        if (this.isPostCreator) return false
        if (this.$store.getters.isOwner || this.$store.getters.isMember) return false
        if (this.communityId && this.communityManagers.some((m: any) => Number(m.id) === this.currentUserId)) return false
        return this.contributors.some((c: any) => Number(c.id) === this.currentUserId)
      },
      canAutoGenerateContent() {
        // Always show the auto-generate input
        return true
      },
      hasRequiredContentForGeneration() {
        // Check if there's enough content to generate from (title or excerpt)
        return (this.form.title.trim().length > 0 ||
          this.form.excerpt.trim().length > 0)
      },
      hasContent() {
        // Check if there's substantial content (more than just empty tags)
        const cleanContent = (this.form.body || '').replace(/<[^>]*>/g, '').trim()
        return cleanContent.length > 5
      },
      isGenerateButton() {
        return !this.hasContent
      },
      isRegenerateButton() {
        return this.hasContent
      },
      generateButtonLabel() {
        if (this.isGenerateButton) {
          return this.$t('knowledge_base_generate_content')
        } else if (this.isRegenerateButton && this.showGenerationInput) {
          return this.$t('knowledge_base_regenerate')
        }
        return this.$t('knowledge_base_regenerate_content')
      }
    },
    async mounted() {
      await this.loadCategories()
      await this.loadCommunities()
      await this.loadSpottingAreas()
      await this.loadPortfolios()

      // Set default community if creating in a specific community context
      if (this.communityId && !this.isEditing) {
        this.form.community_ids = [parseInt(this.communityId)]
      }

      // Set default spotting area if creating in a specific spotting area context
      if (this.spottingAreaId && !this.isEditing) {
        this.form.spotting_area_ids = [parseInt(this.spottingAreaId)]
      }

      // Load existing data if editing
      if (this.isEditing) {
        await this.loadExistingPost()
        await this.loadContributors()
        this.startEditingSession()
        await this.acquireEditLock()
      }

      document.addEventListener('click', this.handleClickOutside)
      this.$bus.on('category-added', this.onCategoryAdded)

      this.$bus.on('confirmAction', (context) => {
        if (context.modalContextType === 'knowledge_base_post_creation_success') {
          this.routeToCorrectKnowledgeBaseRoute()
        }
      })
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside)
      this.$bus.off('category-added', this.onCategoryAdded)
      this.$bus.off('confirmAction')
      this.stopEditingSession()
      this.releaseEditLock()
    },
    watch: {
      'tempLandscapeConfig.color': {
        handler() {
          console.log('hoi')
          this.updateCircleColor()
        },
      },
      'form.category_id': {
        handler(newCategoryId, oldCategoryId) {
          if (newCategoryId !== oldCategoryId) {
            this.loadMapsUsingCategory(newCategoryId)
          }
        },
        immediate: false
      }
    },
    methods: {
      // ===== Contributors =====
      async loadContributors() {
        if (!this.documentId) return
        try {
          const data = await fetchContributors(Number(this.documentId))
          this.contributors = data.contributors || []
          this.communityManagers = data.community_managers || []
        } catch (e) {
          console.error('Failed to load contributors', e)
        }
      },

      // ===== Editing presence (cosmetic polling) =====
      startEditingSession() {
        if (!this.documentId) return

        const postId = Number(this.documentId)
        const heartbeat = () => {
          startEditing(postId).then((data: any) => {
            this.activeEditors = data.editors || []
          }).catch(() => {
          })
        }

        heartbeat()
        this.editingHeartbeatInterval = setInterval(heartbeat, 20000)

        // Autosave form to localStorage every 15s so a refresh never loses work
        this.autosaveInterval = setInterval(() => this.saveDraftToStorage(), 15000)
      },

      stopEditingSession() {
        if (this.editingHeartbeatInterval) {
          clearInterval(this.editingHeartbeatInterval)
          this.editingHeartbeatInterval = null
        }
        if (this.autosaveInterval) {
          clearInterval(this.autosaveInterval)
          this.autosaveInterval = null
        }
        if (this.documentId) {
          stopEditing(Number(this.documentId)).catch(() => {
          })
        }
        this.activeEditors = []
      },

      // ===== Exclusive edit lock =====
      async acquireEditLock(): Promise<boolean> {
        if (!this.documentId) return false
        try {
          const data = await acquireLock(Number(this.documentId))
          const wasBlocked = !!this.lockHolder
          this.lockToken = data.token
          this.lockHolder = null
          // Stop polling now that we own the lock
          if (this.lockPollInterval) {
            clearInterval(this.lockPollInterval)
            this.lockPollInterval = null
          }
          // Reload post to get the latest saved version before the user starts editing
          if (wasBlocked) {
            await this.loadExistingPost()
          }
          // Refresh lock every 3 minutes (TTL is 10 min)
          this.lockRefreshInterval = setInterval(async () => {
            try {
              await refreshLock(Number(this.documentId), this.lockToken)
            } catch {
              this.lockToken = null
              clearInterval(this.lockRefreshInterval)
              this.lockRefreshInterval = null
              // Lost the lock — retry acquisition
              this.acquireEditLock()
            }
          }, 3 * 60 * 1000)
          return true
        } catch (err: any) {
          // 423 → locked by another user; poll every 30s to retry
          const status = await getLockStatus(Number(this.documentId)).catch(() => null)
          this.lockHolder = status?.locked_by || null
          this.lockToken = null
          if (!this.lockPollInterval) {
            this.lockPollInterval = setInterval(() => this.acquireEditLock(), 5 * 1000)
          }
          return false
        }
      },

      async releaseEditLock() {
        if (this.lockRefreshInterval) {
          clearInterval(this.lockRefreshInterval)
          this.lockRefreshInterval = null
        }
        if (this.lockPollInterval) {
          clearInterval(this.lockPollInterval)
          this.lockPollInterval = null
        }
        if (this.documentId && this.lockToken) {
          await releaseLock(Number(this.documentId), this.lockToken).catch(() => {
          })
          this.lockToken = null
        }
      },

      saveDraftToStorage() {
        if (!this.documentId) return
        localStorage.setItem(`kb_draft_${this.documentId}`, JSON.stringify({
          title: this.form.title,
          body: this.form.body,
          excerpt: this.form.excerpt,
          category_id: this.form.category_id,
          visibility: this.form.visibility,
          community_ids: this.form.community_ids,
          spotting_area_ids: this.form.spotting_area_ids,
          show_in_public_knowledge_base: this.form.show_in_public_knowledge_base,
        }))
      },

      clearDraftFromStorage() {
        if (this.documentId) {
          localStorage.removeItem(`kb_draft_${this.documentId}`)
        }
      },


      handleCommunityIdsChange(newIds) {
        // Ensure context community cannot be removed
        if (this.communityId && !newIds.includes(this.communityId)) {
          this.form.community_ids = [this.communityId, ...newIds]
        } else {
          this.form.community_ids = newIds
        }
      },
      handleSpottingAreaIdsChange(newIds) {
        // Ensure context spotting area cannot be removed
        if (this.spottingAreaId && !newIds.includes(this.spottingAreaId)) {
          this.form.spotting_area_ids = [this.spottingAreaId, ...newIds]
        } else {
          this.form.spotting_area_ids = newIds
        }
      },
      async loadExistingPost() {
        try {
          const response = await fetchPost(this.documentId, this.communityId)
          const post = response

          // Populate form with existing data
          this.postCreatorId = post.creator_id || post.creatorId || null
          if (post.creator) {
            this.postCreator = { ...post.creator, photo_url: post.creator.photoUrl ?? post.creator.photo_url ?? null }
          }
          this.lastKnownUpdatedAt = post.updatedAt || post.updated_at || null

          // Restore draft saved before a conflict reload
          const savedDraft = localStorage.getItem(`kb_draft_${this.documentId}`)
          if (savedDraft) {
            try {
              const draft = JSON.parse(savedDraft)
              if (draft.title !== undefined) this.form.title = draft.title
              if (draft.body !== undefined) this.form.body = draft.body
              if (draft.excerpt !== undefined) this.form.excerpt = draft.excerpt
              if (draft.category_id !== undefined) this.form.category_id = draft.category_id
              if (draft.visibility !== undefined) this.form.visibility = draft.visibility
              if (draft.community_ids !== undefined) this.form.community_ids = draft.community_ids
              if (draft.spotting_area_ids !== undefined) this.form.spotting_area_ids = draft.spotting_area_ids
              if (draft.show_in_public_knowledge_base !== undefined) this.form.show_in_public_knowledge_base = draft.show_in_public_knowledge_base
              this.showDraftRestoredBanner = true
            } catch (e) {
              // ignore malformed draft
            } finally {
              localStorage.removeItem(`kb_draft_${this.documentId}`)
            }
          }
          this.form = {
            title: post.title || '',
            body: post.body || '',
            category_id: post.category?.id || '',
            visibility: post.isPublic ? 'public' : 'private',
            excerpt: post.excerpt || '',
            community_ids: post.communityIds || [],
            spotting_area_ids: post.spottingAreaIds || [],
            show_in_public_knowledge_base: post.showInPublicKnowledgeBase || false,
            is_landscape: post.isLandscape || false,
            landscape_lat: post.landscapeLat || null,
            landscape_lng: post.landscapeLng || null,
            landscape_range: post.landscapeRange || null,
            landscape_portfolio_id: post.portfolioId || null,
            landscape_color: post.landscapeColor || null,
            landscape_circle_hidden: post.landscapeCircleHidden || false,
          }

          // Set existing files and URLs
          if (post.files && post.files.length > 0) {
            this.uploadedFiles = post.files.map(file => ({
              id: file.id,
              name: file.title || file.originalName, // Prioritize title over originalName
              size: file.size,
              type: file.mimetype,
              url: file.url || file.path,
              isExisting: true, // Mark as existing file
              isFeatured: file.isFeatured || false, // Track featured status
              displayName: file.title || null // Use title as display name
            }))

            // Featured status is already set in the file objects via isFeatured property
          }

          // Set thumbnail if exists
          if (post.thumbnail) {
            this.featuredImagePreview = post.thumbnail
            this.originallyHadThumbnail = true
          }

        } catch (error) {
          console.error('Failed to load existing post:', error)
          this.$notify({
            group: 'alerts',
            type: 'error',
            title: this.$t('error'),
            text: this.$t('knowledge_base_failed_to_load_post')
          })
        }
      },
      async loadCategories() {
        try {
          const response = await fetchCategories()
          this.categories = response.data
        } catch (error) {
          console.error('Failed to load categories:', error)
          // Set empty array as fallback
          this.categories = []
        }
      },
      async loadCommunities() {
        try {
          await this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITIES_LIST)
          if (this.isOwner || this.isMember) {
            this.availableCommunities = this.$store.state.communities.listData.data || []
            return
          }

          this.availableCommunities = this.joinedCommunities
        } catch (error) {
          console.error('Failed to load communities:', error)
          this.availableCommunities = []
        }
      },
      async loadSpottingAreas() {
        try {
          await this.$store.dispatch(ACTION_TYPES.FETCH_SPOTTING_AREAS_LIST)
          if (this.isOwner || this.isMember) {
            this.availableSpottingAreas = this.spottingAreas
            return
          }

          this.availableSpottingAreas = this.joinedSpottingAreas
        } catch (error) {
          console.error('Failed to load communities:', error)
          this.availableSpottingAreas = []
        }
      },
      async loadPortfolios() {
        try {
          Portfolios.get()
            .then(portfolios => {
              this.availablePortfolios = portfolios
            })
            .catch(errors => {
              this.errors = errors
            })
        } catch (error) {
          console.error('Failed to load portfolios:', error)
          this.availablePortfolios = []
        }
      },
      async loadMapsUsingCategory(categoryId) {
        if (!categoryId || categoryId === 'uncategorized') {
          this.mapsUsingCategory = []
          return
        }

        try {
          this.loadingMapsUsage = true
          const response = await fetch(`/api/settings/ecosystem-maps/category/${categoryId}/usage`)
          if (!response.ok) {
            throw new Error('Failed to fetch maps using category')
          }
          const data = await response.json()
          this.mapsUsingCategory = data.maps || []
        } catch (error) {
          console.error('Failed to load maps using category:', error)
          this.mapsUsingCategory = []
        } finally {
          this.loadingMapsUsage = false
        }
      },
      getCategoryDisplayName() {
        if (!this.form.category_id || this.form.category_id === null) {
          return this.$t('knowledge_base_no_category')
        }
        if (this.form.category_id === 'uncategorized') {
          return this.$t('knowledge_base_uncategorized')
        }
        const category = this.categories.find(c => c.id === parseInt(this.form.category_id))
        return category ? category.name : this.$t('knowledge_base_unknown_category')
      },
      selectCategory(categoryId) {
        this.form.category_id = categoryId
        this.showCategoryDropdown = false
      },
      getCommunityDisplayName() {
        if (!this.form.community_ids || this.form.community_ids.length === 0) {
          return this.$t('knowledge_base_no_community')
        }

        if (this.form.community_ids.length === 1) {
          const community = this.availableCommunities.find(c => c.id === this.form.community_ids[0])
          return community ? community.name : this.$t('knowledge_base_no_community')
        }

        // Multiple communities selected
        const selectedCommunities = this.availableCommunities.filter(c =>
          this.form.community_ids.includes(c.id)
        )

        if (selectedCommunities.length === 0) {
          return this.$t('knowledge_base_no_community')
        }

        return selectedCommunities.map(c => c.name).join(', ')
      },
      handleClickOutside(event) {
        const dropdownElements = this.$el.querySelectorAll('.relative')
        let clickedOutside = true

        dropdownElements.forEach(element => {
          if (element.contains(event.target)) {
            clickedOutside = false
          }
        })

        if (clickedOutside) {
          this.showCategoryDropdown = false
          this.showCommunityDropdown = false
        }
      },
      openCreateCategoryModal() {
        this.showCategoryDropdown = false
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_KNOWLEDGE_BASE_CATEGORY)
      },
      onCategoryAdded(newCategory) {
        this.categories.push(newCategory)
        this.form.category_id = newCategory.id
      },
      goBack() {
        if (this.communityId && this.postId) {
          this.$router.push(`/communities/${this.communityId}/knowledge-base/posts/${this.postId}`)
        } else if (this.spottingAreaId && this.postId) {
          this.$router.push(`/spotting-areas/${this.spottingAreaId}/knowledge-base/posts/${this.postId}`)
        } else if (this.postId) {
          this.$router.push(`/knowledge-base/posts/${this.postId}`)
        } else {
          this.$router.push(`/knowledge-base/`)
        }
      },
      handleGenerate() {
        if (this.isGenerateButton) {
          // Direct generation for empty content
          this.autoGenerateContent()
        } else if (this.isRegenerateButton && this.showGenerationInput) {
          // Re-generate with context
          this.autoGenerateContent()
        } else {
          // Show context input for re-generation
          this.showGenerationInput = !this.showGenerationInput
          if (!this.showGenerationInput) {
            this.generationInstructions = ''
          }
        }
      },
      async autoGenerateContent() {
        this.isGeneratingContent = true
        this.errors.body = null

        try {
          // Prepare data for AI generation
          const generationData = {
            title: this.form.title || '',
            excerpt: this.form.excerpt || '',
            files: this.uploadedFiles.map(file => ({
              name: file.name,
              type: file.type
            })),
            userInstructions: this.generationInstructions.trim() || null
          }

          // Use AI to generate content
          const response = await generateContent('knowledge_base_content', generationData)

          // Set the generated content
          this.form.body = response.result

          // Hide the generation input after successful generation
          this.showGenerationInput = false
          this.generationInstructions = ''

          this.$notify({
            group: 'alerts',
            type: 'success',
            title: this.$t('knowledge_base_content_generated'),
            text: this.$t('knowledge_base_content_generated_text')
          })

        } catch (error) {
          console.error('Error generating content:', error)
          this.$notify({
            group: 'alerts',
            type: 'error',
            title: this.$t('knowledge_base_generation_failed'),
            text: this.$t('knowledge_base_generation_failed_text')
          })
        } finally {
          this.isGeneratingContent = false
        }
      },

      // Featured Image methods
      triggerFeaturedImageInput() {
        this.$refs.featuredImageInput?.click()
      },
      handleFeaturedImageSelect(event) {
        const file = event.target.files[0]
        if (file && file.type.startsWith('image/')) {
          this.featuredImage = file
          this.createImagePreview(file)
          // Reset removal flag when new thumbnail is selected
          this.shouldRemoveThumbnail = false
        }
      },
      handleFeaturedImageDrop(event) {
        const file = event.dataTransfer.files[0]
        if (file && file.type.startsWith('image/')) {
          this.featuredImage = file
          this.createImagePreview(file)
        }
      },
      createImagePreview(file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.featuredImagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      },
      removeFeaturedImage() {
        this.featuredImage = null
        this.featuredImagePreview = null
        if (this.$refs.featuredImageInput) {
          this.$refs.featuredImageInput.value = ''
        }

        // If editing and the post originally had a thumbnail, mark for removal
        if (this.isEditing && this.originallyHadThumbnail) {
          this.shouldRemoveThumbnail = true
        }
      },

      // File methods
      triggerFileInput() {
        this.$refs.fileInput?.click()
      },
      handleFileSelect(event) {
        const files = Array.from(event.target.files)
        this.addFiles(files)
      },
      handleFileDrop(event) {
        const files = Array.from(event.dataTransfer.files)
        this.addFiles(files)
      },
      addFiles(files) {
        files.forEach(file => {
          this.uploadedFiles.push({
            name: file.name,
            size: file.size,
            type: file.type,
            file: file
          })
        })
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      },
      addUrl() {
        if (this.urlInput.trim()) {
          this.uploadedFiles.push({
            url: this.urlInput.trim(),
            name: this.getUrlDisplayName(this.urlInput.trim()),
            type: this.detectUrlType(this.urlInput.trim())
          })
          this.urlInput = ''
        }
      },
      removeFile(index) {
        const fileToRemove = this.uploadedFiles[index]

        // If it's an existing file (from the server), track its ID for deletion
        if (this.isEditing && fileToRemove.isExisting && fileToRemove.id) {
          this.removedFileIds.push(fileToRemove.id)
        }

        // If we're removing a featured file, mark it as not featured
        if (fileToRemove.isFeatured) {
          fileToRemove.isFeatured = false
        }
        this.uploadedFiles.splice(index, 1)
      },
      toggleFeatureFile(index, file) {
        if (file.isFeatured) {
          // If currently featured, remove the featured status
          file.isFeatured = false
        } else {
          // Clear any existing featured file first
          this.uploadedFiles.forEach(f => f.isFeatured = false)
          // Set this file as featured
          file.isFeatured = true
        }
      },

      // File name editing methods
      startEditFileName(index, file) {
        // Set up editing state - direct property assignment works in Vue 3
        file.isEditingName = true
        file.editedName = file.displayName || file.name || file.url

        // Focus the input field after Vue updates the DOM
        this.$nextTick(() => {
          // Find the input element using a more reliable method
          const fileRows = document.querySelectorAll('.space-y-2 > div')
          if (fileRows[index]) {
            const input = fileRows[index].querySelector('input[type="text"]')
            if (input) {
              input.focus()
              input.select()
            }
          }
        })
      },

      saveFileName(index, file) {
        if (!file.editedName || file.editedName.trim() === '') {
          // Don't save empty names, cancel instead
          this.cancelEditFileName(index, file)
          return
        }

        // Save the edited name as display name
        file.displayName = file.editedName.trim()
        file.isEditingName = false
        delete file.editedName
      },

      cancelEditFileName(index, file) {
        // Cancel editing without saving
        file.isEditingName = false
        delete file.editedName
      },

      getFeaturedFileName() {
        const featuredFile = this.uploadedFiles.find(file => file.isFeatured)
        if (featuredFile) {
          return featuredFile.name || featuredFile.url || this.$t('knowledge_base_unknown_file')
        }
        return ''
      },

      // Utility methods
      getFileIcon(typeOrName) {
        if (!typeOrName) return 'document'

        const type = typeOrName.toLowerCase()

        // PDF files
        if (type.includes('pdf')) return 'file-pdf'

        // Image files
        if (type.includes('image') || type.includes('jpg') || type.includes('jpeg') || type.includes('png') || type.includes('gif') || type.includes('webp')) return 'file-image'

        // Document files
        if (type.includes('word') || type.includes('doc')) return 'file-word'

        // Spreadsheet files
        if (type.includes('excel') || type.includes('xls') || type.includes('sheet')) return 'file-spreadsheet'

        // Presentation files
        if (type.includes('powerpoint') || type.includes('ppt') || type.includes('presentation')) return 'file-presentation'

        // Audio files
        if (type.includes('audio') || type.includes('mp3') || type.includes('wav') || type.includes('spotify')) return 'file-audio'

        // Video files
        if (type.includes('video') || type.includes('mp4') || type.includes('avi') || type.includes('mov') || type.includes('youtube')) return 'file-video'

        // Archive files
        if (type.includes('zip') || type.includes('rar') || type.includes('7z') || type.includes('tar')) return 'file-zip'

        // Text files
        if (type.includes('text') || type.includes('txt') || type.includes('md') || type.includes('markdown')) return 'file-text'

        // URL links
        if (type.includes('url') || type.includes('link') || type.includes('http')) return 'file-link'

        // Default file icon
        return 'document'
      },
      getFileTypeLabel(fileType) {
        const labelMap = {
          'application/pdf': this.$t('knowledge_base_file_type_pdf'),
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': this.$t('knowledge_base_file_type_excel'),
          'application/vnd.ms-excel': this.$t('knowledge_base_file_type_excel'),
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': this.$t('knowledge_base_file_type_word'),
          'application/msword': this.$t('knowledge_base_file_type_word'),
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': this.$t('knowledge_base_file_type_powerpoint'),
          'application/vnd.ms-powerpoint': this.$t('knowledge_base_file_type_powerpoint'),
          'image/jpeg': this.$t('knowledge_base_file_type_image'),
          'image/png': this.$t('knowledge_base_file_type_image'),
          'image/gif': this.$t('knowledge_base_file_type_image'),
          'audio/mpeg': this.$t('knowledge_base_file_type_audio'),
          'video/mp4': this.$t('knowledge_base_file_type_video'),
          'youtube': this.$t('knowledge_base_file_type_youtube'),
          'spotify': this.$t('knowledge_base_file_type_spotify'),
          'url': this.$t('knowledge_base_file_type_link')
        }
        return labelMap[fileType] || this.$t('knowledge_base_file_type_generic')
      },
      formatFileSize(bytes) {
        if (!bytes || bytes === 0) return '0 ' + this.$t('knowledge_base_file_size_bytes')
        const k = 1024
        const sizes = [this.$t('knowledge_base_file_size_bytes'), this.$t('knowledge_base_file_size_kb'), this.$t('knowledge_base_file_size_mb'), this.$t('knowledge_base_file_size_gb')]
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
      },
      getUrlDisplayName(url) {
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
          return this.$t('knowledge_base_youtube_video')
        }
        if (url.includes('spotify.com')) {
          return this.$t('knowledge_base_spotify_content')
        }
        try {
          const domain = new URL(url).hostname.replace('www.', '')
          return this.$t('knowledge_base_link') + ' - ' + domain
        } catch {
          return this.$t('knowledge_base_external_link')
        }
      },
      detectUrlType(url) {
        if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube'
        if (url.includes('spotify.com')) return 'spotify'
        return 'url'
      },

      // Form validation and submission
      validateForm() {
        this.errors = {}

        // Title validation
        if (!this.form.title.trim()) {
          this.errors.title = this.$t('knowledge_base_validation_title_required')
        } else if (this.form.title.length > 255) {
          this.errors.title = this.$t('knowledge_base_validation_title_max_length')
        }

        // Body validation - only required if not a draft
        const isDraft = this.form.visibility === 'private' && this.form.category_id === 'draft'
        if (!isDraft && (!this.form.body || !this.form.body.trim())) {
          this.errors.body = this.$t('knowledge_base_validation_content_required')
        }

        // Excerpt validation
        if (this.form.excerpt && this.form.excerpt.length > 500) {
          this.errors.excerpt = this.$t('knowledge_base_validation_excerpt_max_length')
        }

        // File size validation
        if (this.featuredImage && this.featuredImage.size > 10 * 1024 * 1024) { // 10MB
          this.errors.thumbnail = this.$t('knowledge_base_validation_thumbnail_max_size')
        }

        // File upload validation
        const oversizedFiles = this.uploadedFiles.filter(file =>
          file.file && file.file.size > 50 * 1024 * 1024 // 50MB
        )
        if (oversizedFiles.length > 0) {
          this.errors.files = this.$t('knowledge_base_file_size_error', { files: oversizedFiles.map(f => f.name).join(', ') })
        }

        // URL validation (only validate non-existing files that are meant to be URLs)
        const invalidUrls = this.uploadedFiles.filter(file =>
          file.url && !file.isExisting && !this.isValidUrl(file.url)
        )
        if (invalidUrls.length > 0) {
          this.errors.urls = this.$t('knowledge_base_invalid_urls_error', { urls: invalidUrls.map(f => f.url).join(', ') })
        }

        return Object.keys(this.errors).length === 0
      },

      isValidUrl(string) {
        try {
          new URL(string)
          return true
        } catch (_) {
          return false
        }
      },
      routeToCorrectKnowledgeBaseRoute(postId: number | null) {
        // Determine the correct base route based on context
        let baseRoute = this.communityId
          ? `/communities/${this.communityId}/knowledge-base/`
          : this.spottingAreaId
            ? `/spotting-areas/${this.spottingAreaId}/knowledge-base/`
            : `/dashboard-knowledge-base/`

        if (!postId) {
          if (baseRoute == '/dashboard-knowledge-base/') {
            // NOTE: this needs to be refactored, but the knowledge base post detail is available on /knowledge-base/... and the KB overview is on /dashboards/knowledge-base/
            baseRoute = '/dashboards/knowledge-base/'
          }

          this.$router.push(baseRoute)

          return
        }

        if (baseRoute == '/dashboard-knowledge-base/') {
          // NOTE: this needs to be refactored, but the knowledge base post detail is available on /knowledge-base/... and the KB overview is on /dashboards/knowledge-base/
          baseRoute = '/dashboards/knowledge-base/'
        }

        this.$router.push(`${baseRoute}posts/${postId}`)
      },
      async submitForm() {
        if (!this.validateForm()) return

        // When editing: ensure we own the exclusive DB lock before saving
        if (this.isEditing && !this.lockToken) {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
            title: this.$t('knowledge_base_save_locked_title'),
            body: this.lockHolder
              ? `${this.lockHolder.name} ${this.$t('knowledge_base_save_locked_message')}`
              : this.$t('knowledge_base_save_locked_message'),
            confirmLabel: this.$t('knowledge_base_curation_confirmation_ok'),
            isCancellable: false,
          })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
          return
        }

        this.saving = true
        try {
          // Debug: Log form data before sending
          const formData = new FormData()
          formData.append('title', this.form.title)
          formData.append('body', this.form.body)
          formData.append('excerpt', this.form.excerpt || '')
          formData.append('is_public', this.form.visibility === 'public' ? '1' : '0')
          formData.append('show_in_public_knowledge_base', this.form.show_in_public_knowledge_base ? '1' : '0')

          // Add category (always send, even if null to explicitly clear)
          if (this.form.category_id && this.form.category_id !== 'uncategorized') {
            formData.append('knowledge_base_category_id', this.form.category_id)
          } else {
            formData.append('knowledge_base_category_id', '')
          }

          // Append the community and spotting areas
          if (this.form.community_ids && this.form.community_ids.length > 0) {
            this.form.community_ids.forEach((communityId, index) => {
              formData.append(`community_ids[${index}]`, communityId)
            })
          }

          if (this.form.spotting_area_ids && this.form.spotting_area_ids.length > 0) {
            this.form.spotting_area_ids.forEach((spottingAreaId, index) => {
              formData.append(`spotting_area_ids[${index}]`, spottingAreaId)
            })
          }

          // Add landscape data
          if (this.form.is_landscape) {
            formData.append('is_landscape', '1')
            if (this.form.landscape_lat) {
              formData.append('landscape_lat', this.form.landscape_lat)
            }
            if (this.form.landscape_lng) {
              formData.append('landscape_lng', this.form.landscape_lng)
            }
            if (this.form.landscape_range) {
              formData.append('landscape_range', this.form.landscape_range)
            }
            if (this.form.landscape_color) {
              formData.append('landscape_color', this.form.landscape_color)
            }
            formData.append('landscape_circle_hidden', this.form.landscape_circle_hidden ? '1' : '0')
          } else {
            formData.append('is_landscape', '0')
          }

          // Add featured image/thumbnail
          if (this.featuredImage) {
            formData.append('thumbnail', this.featuredImage)
          }

          // Add remove_thumbnail flag if editing and thumbnail should be removed
          if (this.isEditing && this.shouldRemoveThumbnail) {
            formData.append('remove_thumbnail', '1')
          }

          if (this.form.landscape_portfolio_id) {
            formData.append('portfolio_id', this.form.landscape_portfolio_id)
          }

          // Add files
          const files = []
          const urls = []
          const fileNames = []
          const urlNames = []

          this.uploadedFiles.forEach((fileItem) => {
            if (fileItem.file) {
              // New uploaded file
              files.push(fileItem.file)
              fileNames.push(fileItem.displayName || null)
            } else if (fileItem.url && !fileItem.isExisting) {
              // New URL (not existing file)
              urls.push(fileItem.url)
              urlNames.push(fileItem.displayName || null)
            }
            // Skip existing files - they're already saved
          })

          // Append files array - Laravel expects 'files[]' for array handling
          files.forEach((file, index) => {
            formData.append(`files[${index}]`, file)
          })

          // Append URLs array - Laravel expects 'urls[]' for array handling
          urls.forEach((url, index) => {
            formData.append(`urls[${index}]`, url)
          })

          // Append file names for new files
          fileNames.forEach((name, index) => {
            if (name) {
              formData.append(`file_names[${index}]`, name)
            }
          })

          // Append URL names for new URLs
          urlNames.forEach((name, index) => {
            if (name) {
              formData.append(`url_names[${index}]`, name)
            }
          })

          // Send file featured status information
          if (this.uploadedFiles.length > 0) {
            // For existing files (when editing)
            const existingFiles = this.uploadedFiles
              .filter(file => file.isExisting)
              .map(file => ({
                id: file.id,
                isFeatured: !!file.isFeatured,
                displayName: file.displayName || null
              }))

            if (existingFiles.length > 0) {
              formData.append('existing_files', JSON.stringify(existingFiles))
            }

            // For new files, we need to track which one should be featured
            // The backend processes files first, then URLs, so we need to calculate the index accordingly
            let featuredNewFileIndex = -1
            let currentIndex = 0

            // Check actual files first (these get processed first on backend)
            for (const fileItem of this.uploadedFiles.filter(f => !f.isExisting)) {
              if (fileItem.file) {
                if (fileItem.isFeatured) {
                  featuredNewFileIndex = currentIndex
                  break
                }
                currentIndex++
              }
            }

            // If no file was featured, check URLs (these get processed after files on backend)
            if (featuredNewFileIndex === -1) {
              for (const fileItem of this.uploadedFiles.filter(f => !f.isExisting)) {
                if (fileItem.url && !fileItem.file) {
                  if (fileItem.isFeatured) {
                    featuredNewFileIndex = currentIndex
                    break
                  }
                  currentIndex++
                }
              }
            }

            if (featuredNewFileIndex !== -1) {
              formData.append('featured_new_file_index', featuredNewFileIndex.toString())
            }
          }

          // Note: Featured file handling is now done via existing_files JSON data above
          // No need for featured_file_index anymore

          // Add removed file IDs if editing and files were removed
          if (this.isEditing && this.removedFileIds.length > 0) {
            this.removedFileIds.forEach((fileId, index) => {
              formData.append(`remove_file_ids[${index}]`, fileId)
            })
          }

          // Send lock token so the backend can verify the caller still owns the lock
          if (this.isEditing && this.lockToken) {
            formData.append('lock_token', this.lockToken)
          }

          // API call to create or update post
          let response

          if (this.isEditing) {
            response = await updatePost(this.documentId, formData)
            trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.EDIT_KNOWLEDGE_BASE_RESOURCE, this.documentId)
          } else {
            response = await createPost(formData)
            trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.CREATE_KNOWLEDGE_BASE_RESOURCE)
          }

          // Handle redirect based on curation status
          if (!this.isEditing && response.curationStatus === 'pending') {
            // Show confirmation modal for pending posts, then redirect to knowledge base home
            this.showCurationConfirmationModal(response)
          } else {

            // Normal redirect to the post for approved posts or edits
            const postId = this.isEditing ? this.documentId : response.id

            // Route back to the appropriate context (general KB, community, or spotting area)
            this.clearDraftFromStorage()
            await this.releaseEditLock()
            this.routeToCorrectKnowledgeBaseRoute(postId)
          }
        } catch (error) {
          console.error('Error saving knowledge base post:', error)

          // Handle stale overwrite (optimistic lock conflict)
          if (error?.conflict === true) {
            this.saveDraftToStorage()
            this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
              title: this.$t('knowledge_base_conflict_title'),
              body: this.$t('knowledge_base_conflict_message'),
              confirmLabel: this.$t('knowledge_base_conflict_reload'),
              isCancellable: false,
            })
            this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
            this.$bus.on('confirmAction', () => {
              this.$bus.off('confirmAction')
              window.location.reload()
            })
            return
          }

          // Handle save-lock (concurrent editor blocked the save)
          if (error?.statusCode === 423 || error?.locked === true) {
            this.saveLocked = true
            this.lockingEditors = error?.other_editors || []
            const editorName = this.lockingEditors[0]?.name || this.$t('knowledge_base_save_locked_title')
            this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
              title: this.$t('knowledge_base_save_locked_title'),
              body: `${editorName} ${this.$t('knowledge_base_save_locked_message')}`,
              confirmLabel: this.$t('knowledge_base_curation_confirmation_ok'),
              isCancellable: false,
            })
            this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
            return
          }

          // Handle validation errors from backend
          if (error.response?.status === 422 && error.response?.data?.errors) {
            // Laravel validation errors
            const validationErrors = error.response.data.errors
            this.errors = {}

            Object.keys(validationErrors).forEach(field => {
              this.errors[field] = Array.isArray(validationErrors[field])
                ? validationErrors[field][0]
                : validationErrors[field]
            })

            this.$notify({
              group: 'alerts',
              type: 'error',
              title: this.$t('knowledge_base_error_validation'),
              text: this.$t('knowledge_base_error_validation_text')
            })
          } else {
            // Show generic error message
            const errorMessage = error.response?.data?.message || (this.isEditing ? this.$t('knowledge_base_error_update_failed') : this.$t('knowledge_base_error_create_failed'))
            this.$notify({
              group: 'alerts',
              type: 'error',
              title: this.$t('knowledge_base_error_title'),
              text: errorMessage
            })
          }
        } finally {
          this.saving = false
        }
      },
      showCurationConfirmationModal(response) {
        // Set up the modal context with our curation message and callback
        const modalContextData = {
          modalContextType: 'knowledge_base_post_creation_success',
          title: this.$t('knowledge_base_curation_confirmation_title'),
          body: this.$t('knowledge_base_curation_confirmation_message'),
          confirmLabel: this.$t('knowledge_base_curation_confirmation_ok'),
          cancelLabel: null, // Hide cancel button for info modal
          postId: response.id
        }

        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, modalContextData)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },

      // City Search Methods
      async searchCityInModal() {
        if (!this.citySearchQuery.trim()) {
          return
        }

        this.searchingCity = true
        this.citySearchResults = []
        try {
          // Use internal GeoNames API
          const query = encodeURIComponent(this.citySearchQuery.trim())
          const response = await fetch(`/api/geo/search-city?city=${query}`)

          if (!response.ok) {
            throw new Error('Failed to search for city')
          }

          const data = await response.json()

          if (data.cities && data.cities.length > 0) {
            this.citySearchResults = data.cities
          } else {
            this.$notify({
              group: 'alerts',
              type: 'warning',
              title: this.$t('knowledge_base_no_results'),
              text: this.$t('knowledge_base_city_not_found', { query: this.citySearchQuery })
            })
          }
        } catch (error) {
          console.error('Error searching for city:', error)
          this.$notify({
            group: 'alerts',
            type: 'error',
            title: this.$t('error'),
            text: this.$t('knowledge_base_city_search_failed')
          })
        } finally {
          this.searchingCity = false
        }
      },

      async selectCity(city) {
        const lat = parseFloat(city.lat)
        const lng = parseFloat(city.lng)

        // Update the temp config
        this.tempLandscapeConfig.lat = lat
        this.tempLandscapeConfig.lng = lng

        // If range is not set, set a default
        if (!this.tempLandscapeConfig.range || this.tempLandscapeConfig.range === 0) {
          this.tempLandscapeConfig.range = 25 // Default 25km for city searches
        }

        // Update the map view if it exists
        if (this.leafletMap) {
          this.leafletMap.setView([lat, lng], 10)

          // Update circle position and radius
          if (this.landscapeCircle) {
            this.landscapeCircle.setLatLng([lat, lng])
            this.landscapeCircle.setRadius(this.tempLandscapeConfig.range * 1000)
          }
        }

        this.$notify({
          group: 'alerts',
          type: 'success',
          title: this.$t('success'),
          text: `Centered map on ${city.display_name}`
        })

        // Clear search query and results
        this.citySearchQuery = ''
        this.citySearchResults = []
      },

      // Landscape Map Modal Methods
      openLandscapeMapModal() {
        // Copy current config to temp config or use defaults
        this.tempLandscapeConfig = {
          lat: this.form.landscape_lat || 50.0,
          lng: this.form.landscape_lng || 15.0,
          range: this.form.landscape_range || 5,
          color: this.form.landscape_color || '#3b82f6'
        }
        this.showLandscapeMapModal = true

        // Initialize map after modal is shown
        this.$nextTick(() => {
          this.initializeLandscapeLeafletMap()
        })
      },

      closeLandscapeMapModal() {
        this.showLandscapeMapModal = false
        if (this.leafletMap) {
          this.leafletMap.remove()
          this.leafletMap = null
        }
        this.landscapeCircle = null
      },

      applyLandscapeMapSettings() {
        // Apply temp config to actual form
        this.form.landscape_lat = this.tempLandscapeConfig.lat
        this.form.landscape_lng = this.tempLandscapeConfig.lng
        this.form.landscape_range = this.tempLandscapeConfig.range
        this.form.landscape_color = this.tempLandscapeConfig.color
        this.closeLandscapeMapModal()
      },

      async initializeLandscapeLeafletMap() {
        try {
          // Dynamically import Leaflet
          const L = await import('leaflet')

          // Import Leaflet CSS
          if (!document.querySelector('link[href*="leaflet"]')) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
            document.head.appendChild(link)
          }

          // Fix Leaflet marker icon paths issue with bundlers
          delete L.Icon.Default.prototype._getIconUrl
          L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
          })

          // Initialize map
          this.leafletMap = L.map('landscape-map-selector').setView(
            [this.tempLandscapeConfig.lat, this.tempLandscapeConfig.lng],
            8
          )

          // Add tile layer
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(this.leafletMap)

          // Add center marker
          let centerMarker = L.marker([this.tempLandscapeConfig.lat, this.tempLandscapeConfig.lng])
            .addTo(this.leafletMap)

          // Add circle to visualize range
          this.landscapeCircle = L.circle(
            [this.tempLandscapeConfig.lat, this.tempLandscapeConfig.lng],
            {
              color: this.tempLandscapeConfig.color,
              fillColor: this.tempLandscapeConfig.color,
              fillOpacity: 0.2,
              radius: this.tempLandscapeConfig.range * 1000 // Convert km to meters
            }
          ).addTo(this.leafletMap)

          // Update config when map moves
          this.leafletMap.on('moveend', () => {
            const center = this.leafletMap.getCenter()
            this.tempLandscapeConfig.lat = center.lat
            this.tempLandscapeConfig.lng = center.lng

            // Update marker and circle position
            centerMarker.setLatLng([center.lat, center.lng])
            this.landscapeCircle.setLatLng([center.lat, center.lng])
          })

          // Click to set center
          this.leafletMap.on('click', (e) => {
            this.leafletMap.setView([e.latlng.lat, e.latlng.lng])
            centerMarker.setLatLng([e.latlng.lat, e.latlng.lng])
            this.landscapeCircle.setLatLng([e.latlng.lat, e.latlng.lng])
          })

        } catch (error) {
          console.error('Failed to initialize Leaflet map:', error)
          this.$notify({
            group: 'alerts',
            type: 'error',
            title: this.$t('error'),
            text: this.$t('knowledge_base_failed_to_load_map')
          })
        }
      },
      updateCircleColor() {
        console.log(this.landscapeCircle, this.tempLandscapeConfig.color)
        if (this.landscapeCircle && this.tempLandscapeConfig.color) {
          // Update circle radius (convert km to meters)
          console.log('updating color')
          this.landscapeCircle.setStyle({
            color: this.tempLandscapeConfig.color,
            fillColor: this.tempLandscapeConfig.color
          })
        }
      },

      updateCircleRadius() {
        if (this.landscapeCircle && this.tempLandscapeConfig.range) {
          // Update circle radius (convert km to meters)
          this.landscapeCircle.setRadius(this.tempLandscapeConfig.range * 1000)
        }
      },

      incrementRange() {
        // Smart increment: smaller steps for small values, larger for big values
        const increment = this.tempLandscapeConfig.range < 10 ? 1 :
          this.tempLandscapeConfig.range < 50 ? 5 :
            this.tempLandscapeConfig.range < 100 ? 10 : 25
        this.tempLandscapeConfig.range = Math.min(1000, this.tempLandscapeConfig.range + increment)
        this.updateCircleRadius()
      },

      decrementRange() {
        // Smart decrement: smaller steps for small values, larger for big values
        const decrement = this.tempLandscapeConfig.range <= 10 ? 1 :
          this.tempLandscapeConfig.range <= 50 ? 5 :
            this.tempLandscapeConfig.range <= 100 ? 10 : 25
        this.tempLandscapeConfig.range = Math.max(0.5, this.tempLandscapeConfig.range - decrement)
        this.updateCircleRadius()
      },

      setRangePreset(value) {
        this.tempLandscapeConfig.range = value
        this.updateCircleRadius()
      }
    }
  })
</script>

<style scoped>
  .prose {
    color: #374151;
  }

  /* Custom styles for inline form elements */
  input[type="text"].text-2xl {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  @media (min-width: 1024px) {
    input[type="text"].lg\:text-3xl {
      font-size: 1.875rem;
      line-height: 2.25rem;
    }
  }

  /* Custom Range Slider Styles */
  input[type="range"].slider {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
  }

  input[type="range"].slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease-in-out;
    position: relative;
    z-index: 10;
  }

  input[type="range"].slider::-webkit-slider-thumb:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    transform: scale(1.15);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  input[type="range"].slider::-webkit-slider-thumb:active {
    transform: scale(1.05);
  }

  input[type="range"].slider::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease-in-out;
  }

  input[type="range"].slider::-moz-range-thumb:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    transform: scale(1.15);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  input[type="range"].slider::-moz-range-thumb:active {
    transform: scale(1.05);
  }

  input[type="range"].slider::-webkit-slider-runnable-track {
    width: 100%;
    height: 12px;
    cursor: pointer;
    background: linear-gradient(to right, rgba(59, 130, 246, 0.2), rgba(147, 197, 253, 0.3));
    border-radius: 9999px;
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  input[type="range"].slider::-moz-range-track {
    width: 100%;
    height: 12px;
    cursor: pointer;
    background: linear-gradient(to right, rgba(59, 130, 246, 0.2), rgba(147, 197, 253, 0.3));
    border-radius: 9999px;
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  input[type="range"].slider:focus {
    outline: none;
  }

  input[type="range"].slider:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  input[type="range"].slider:focus::-moz-range-thumb {
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(59, 130, 246, 0.4);
  }
</style>
